import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api, { formatDate, STATUS_LABELS } from '../lib/api';
import { Check, Clock, Calendar, Users, AlertTriangle, MapPin, Phone, X } from 'lucide-react';
import { toast } from 'sonner';
import BrandLogo from '../components/BrandLogo';

export default function SelfService() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [callOpen, setCallOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/reservations/${id}/self-service`);
      setData(r.data);
    } catch {
      setError(true);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleCancelClick = () => {
    if (!data?.can_cancel) {
      setCallOpen(true);
    } else {
      setConfirmOpen(true);
    }
  };

  const doCancel = async () => {
    setBusy(true);
    try {
      await api.post(`/reservations/${id}/customer-cancel`);
      toast.success('Reservierung storniert');
      setConfirmOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler bei der Stornierung');
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F7]">
        <div className="text-center" data-testid="selfservice-not-found">
          <p className="text-[#5C5C59] mb-4">Reservierung nicht gefunden.</p>
          <Link to="/" className="btn-primary px-6 py-3 rounded">Zurück</Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F7]">
        <div className="w-10 h-10 border-2 border-[#1C1C19] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const r = data.reservation;
  const isCancelled = r.status === 'cancelled' || r.status === 'declined';
  const isCompleted = r.status === 'completed' || r.status === 'no_show';
  const phone = data.restaurant_phone || '';

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="selfservice-page">
      <div className="max-w-2xl mx-auto px-4 md:px-6 py-8 md:py-16">
        <div className="mb-10 md:mb-12">
          <BrandLogo
            logoUrl={data.logo_url}
            logoText={data.logo_text || data.restaurant_name}
            testId="ss-logo"
          />
        </div>

        <div className="bg-white border border-[#E5E5E0] rounded-lg overflow-hidden">
          <div className="p-6 md:p-10">
            <div className="flex items-center gap-3 mb-6">
              {isCancelled ? (
                <span className="w-12 h-12 rounded-full bg-[#EF4444]/15 flex items-center justify-center">
                  <X className="w-6 h-6 text-[#EF4444]" />
                </span>
              ) : isCompleted ? (
                <span className="w-12 h-12 rounded-full bg-[#9CA3AF]/20 flex items-center justify-center">
                  <Check className="w-6 h-6 text-[#5C5C59]" />
                </span>
              ) : (
                <span className="w-12 h-12 rounded-full bg-[#22C55E]/15 flex items-center justify-center">
                  <Check className="w-6 h-6 text-[#22C55E]" />
                </span>
              )}
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#5C5C59]" data-testid="ss-status-label">
                  {STATUS_LABELS[r.status] || r.status}
                </p>
                <h1 className="font-display text-2xl md:text-3xl text-[#1C1C19]" data-testid="ss-title">
                  Ihre Reservierung
                </h1>
              </div>
            </div>

            <div className="border-t border-[#E5E5E0] pt-6 space-y-4">
              <Row icon={Calendar} label="Datum" value={formatDate(r.date)} testId="ss-date" />
              <Row icon={Clock} label="Uhrzeit" value={`${r.start_time} – ${r.end_time}`} testId="ss-time" />
              <Row icon={Users} label="Personen" value={r.persons} testId="ss-persons" />
              <Row icon={MapPin} label="Name" value={r.customer_name} testId="ss-name" />
              {r.note && (
                <div className="border-t border-[#E5E5E0] pt-4">
                  <p className="text-xs uppercase tracking-wider text-[#5C5C59] mb-1">Ihre Bemerkung</p>
                  <p className="text-sm text-[#1C1C19]">{r.note}</p>
                </div>
              )}
            </div>

            {!isCancelled && !isCompleted && (
              <div className="mt-8 pt-6 border-t border-[#E5E5E0]">
                <button
                  onClick={handleCancelClick}
                  data-testid="ss-cancel-btn"
                  className="w-full md:w-auto px-6 py-3 border border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444] hover:text-white rounded text-sm font-medium transition-colors"
                >
                  Reservierung stornieren
                </button>
                <p className="text-xs text-[#5C5C59] mt-2">
                  {data.can_cancel
                    ? 'Online-Stornierung möglich.'
                    : `Online-Stornierung nicht mehr möglich (weniger als ${data.cancel_hours_limit} Stunden vor Reservierung).`}
                </p>
              </div>
            )}

            {isCancelled && (
              <div className="mt-6 px-4 py-3 bg-[#FEE2E2] border border-[#EF4444]/30 rounded text-sm text-[#1C1C19]" data-testid="ss-cancelled-info">
                Diese Reservierung ist storniert.
              </div>
            )}

            <div className="mt-8 pt-6 border-t border-[#E5E5E0] text-sm text-[#5C5C59]">
              <p className="font-mono">Reservierungsnummer: <span className="text-[#1C1C19]" data-testid="ss-id">{r.id}</span></p>
            </div>
          </div>
        </div>
      </div>

      {/* Confirm cancel dialog */}
      {confirmOpen && (
        <div className="fixed inset-0 bg-[#1C1C19]/40 z-50 flex items-center justify-center p-4" onClick={() => setConfirmOpen(false)}>
          <div className="bg-white rounded-lg max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="ss-confirm-dialog">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-[#EF4444] flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="font-display text-lg text-[#1C1C19]">Reservierung wirklich stornieren?</h2>
                <p className="text-sm text-[#5C5C59] mt-1">
                  Diese Aktion kann nicht rückgängig gemacht werden.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setConfirmOpen(false)}
                data-testid="ss-confirm-cancel-btn"
                className="px-4 py-2 border border-[#E5E5E0] text-[#1C1C19] rounded text-sm"
                disabled={busy}
              >
                Abbrechen
              </button>
              <button
                onClick={doCancel}
                data-testid="ss-confirm-yes-btn"
                disabled={busy}
                className="px-4 py-2 bg-[#EF4444] hover:bg-[#DC2626] text-white rounded text-sm disabled:opacity-50"
              >
                {busy ? 'Storniere...' : 'Ja, stornieren'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cannot cancel - call us dialog */}
      {callOpen && (
        <div className="fixed inset-0 bg-[#1C1C19]/40 z-50 flex items-center justify-center p-4" onClick={() => setCallOpen(false)}>
          <div className="bg-white rounded-lg max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="ss-call-dialog">
            <div className="flex items-start gap-3 mb-4">
              <Phone className="w-6 h-6 text-[#EAB308] flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="font-display text-lg text-[#1C1C19]">Bitte rufen Sie uns an</h2>
                <p className="text-sm text-[#5C5C59] mt-2">
                  Eine Online-Stornierung ist nur bis {data.cancel_hours_limit} Stunden vor der Reservierung möglich.
                  Bitte falls Sie stornieren möchten, rufen Sie uns an:
                </p>
                {phone && (
                  <a
                    href={`tel:${phone.replace(/\s/g, '')}`}
                    data-testid="ss-call-phone"
                    className="inline-flex items-center gap-2 mt-4 px-4 py-2.5 bg-[#1C1C19] hover:bg-[#3A3A35] text-white rounded text-sm font-medium"
                  >
                    <Phone className="w-4 h-4" /> {phone}
                  </a>
                )}
              </div>
            </div>
            <div className="flex justify-end mt-6">
              <button
                onClick={() => setCallOpen(false)}
                data-testid="ss-call-close-btn"
                className="px-4 py-2 border border-[#E5E5E0] text-[#1C1C19] rounded text-sm"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ icon: Icon, label, value, testId }) {
  return (
    <div className="flex items-start gap-3" data-testid={testId}>
      <Icon className="w-5 h-5 text-[#5C5C59] mt-0.5" />
      <div>
        <p className="text-xs uppercase tracking-wider text-[#5C5C59]">{label}</p>
        <p className="text-base text-[#1C1C19] font-medium">{value}</p>
      </div>
    </div>
  );
}
